import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import type { CfaoVehicle } from "@/data/cfaoVehicles";
import { useVehicles } from "@/hooks/useVehicles";

const SITE_URL = "https://simphiwemotors.co.za";
const SITE_NAME = "Simphiwe Motors";
const DEFAULT_IMAGE = `${SITE_URL}/images/home-hero.png`;
const canonicalUrl = (path: string) => `${SITE_URL}${path === "/" ? "/" : `${path.replace(/\/$/, "")}/`}`;

type SeoData = {
  title: string;
  description: string;
  canonical: string;
  image?: string;
  type?: "website" | "product";
  robots?: string;
  schemas?: Record<string, unknown>[];
};

const pageSeo: Record<string, Omit<SeoData, "canonical">> = {
  "/": {
    title: "Used Cars Cape Town | Simphiwe Fudwana at Toyota Tokai",
    description: "Browse quality used cars in Cape Town with personal help from Simphiwe Fudwana at Toyota Tokai. Compare vehicles, prices and finance estimates online.",
  },
  "/vehicles": {
    title: "Used Cars for Sale in Cape Town | Simphiwe Motors",
    description: "Browse quality pre-owned cars for sale at Toyota Tokai in Cape Town. Search by make, model, year, body type, price and transmission.",
  },
  "/about": {
    title: "About Simphiwe Fudwana | Used Car Consultant Cape Town",
    description: "Meet Simphiwe Fudwana, a pre-owned vehicle sales consultant at Toyota Tokai offering personal, straightforward car-buying guidance in Cape Town.",
  },
  "/contact": {
    title: "Contact Simphiwe Fudwana | Toyota Tokai Cape Town",
    description: "Contact Simphiwe Fudwana about used-car availability, finance estimates, trade-ins, test drives and vehicle viewings at Toyota Tokai in Cape Town.",
  },
  "/privacy": {
    title: "Privacy Policy | Simphiwe Motors",
    description: "Read how personal information submitted through the Simphiwe Motors website is collected, used and protected under POPIA.",
  },
};

const businessSchema = {
  "@context": "https://schema.org",
  "@type": ["AutomotiveBusiness", "Organization"],
  "@id": `${SITE_URL}/#business`,
  name: "Simphiwe Fudwana - Pre-Owned Vehicle Sales at Toyota Tokai",
  alternateName: SITE_NAME,
  url: `${SITE_URL}/`,
  image: DEFAULT_IMAGE,
  logo: `${SITE_URL}/favicon.svg`,
  telephone: "+27 68 103 1545",
  email: "simphiwe.fudwana@simphiwemotors.co.za",
  priceRange: "R",
  address: {
    "@type": "PostalAddress",
    streetAddress: "145 Main Road, Tokai",
    addressLocality: "Cape Town",
    postalCode: "7945",
    addressRegion: "Western Cape",
    addressCountry: "ZA",
  },
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "08:00", closes: "17:30" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "08:00", closes: "13:00" },
  ],
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  inLanguage: "en-ZA",
  publisher: { "@id": `${SITE_URL}/#business` },
};

const formatNumber = (value: number) => value.toLocaleString("en-ZA").replace(/\u00a0/g, " ");

function vehicleSeo(pathname: string, vehicles: CfaoVehicle[]): SeoData | null {
  const slug = pathname.split("/").filter(Boolean).pop() || "";
  const vehicle = vehicles.find((item) => item.slug === slug && item.status === "published");
  if (!vehicle) return null;

  const title = vehicle.fullTitle;
  const price = vehicle.price;
  const mileage = vehicle.mileage;
  const image = vehicle.image;
  const color = vehicle.color;
  const canonical = canonicalUrl(`/vehicles/${slug}`);
  const description = `${title} for sale in Cape Town${price ? ` for R ${formatNumber(price)}` : ""}. ${formatNumber(mileage)} km, ${vehicle.transmission.toLowerCase()}, ${vehicle.fuelType.toLowerCase()}. Enquire with Simphiwe at Toyota Tokai.`;

  const product = {
    "@context": "https://schema.org",
    "@type": ["Product", "Vehicle"],
    "@id": `${canonical}#vehicle`,
    name: title,
    description,
    image: image ? [image] : undefined,
    sku: vehicle.id,
    brand: { "@type": "Brand", name: vehicle.make },
    vehicleModelDate: String(vehicle.year),
    vehicleTransmission: vehicle.transmission,
    fuelType: vehicle.fuelType,
    bodyType: vehicle.bodyType,
    color: color || undefined,
    mileageFromOdometer: { "@type": "QuantitativeValue", value: mileage, unitCode: "KMT" },
    offers: price ? {
      "@type": "Offer",
      url: canonical,
      price,
      priceCurrency: "ZAR",
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/UsedCondition",
      seller: { "@id": `${SITE_URL}/#business` },
    } : undefined,
  };
  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Used Cars", item: canonicalUrl("/vehicles") },
      { "@type": "ListItem", position: 3, name: title, item: canonical },
    ],
  };

  return {
    title: `${title} | ${formatNumber(mileage)} km | Cape Town`,
    description,
    canonical,
    image,
    type: "product",
    schemas: [product, breadcrumbs],
  };
}

const setMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element!.setAttribute(key, value));
};

export default function Seo() {
  const { pathname } = useLocation();
  const { vehicles } = useVehicles();
  const data = useMemo<SeoData>(() => {
    const normalized = pathname !== "/" ? pathname.replace(/\/$/, "") : "/";
    const detail = normalized.startsWith("/vehicles/") ? vehicleSeo(normalized, vehicles) : null;
    if (detail) return detail;
    const page = pageSeo[normalized];
    if (page) {
      return {
        ...page,
        canonical: canonicalUrl(normalized),
        schemas: normalized === "/" ? [businessSchema, websiteSchema] : undefined,
      };
    }
    return {
      ...pageSeo["/"],
      canonical: `${SITE_URL}/`,
      robots: "noindex,follow",
    };
  }, [pathname, vehicles]);

  useEffect(() => {
    document.documentElement.lang = "en-ZA";
    document.title = data.title;
    setMeta('meta[name="description"]', { name: "description", content: data.description });
    setMeta('meta[name="robots"]', { name: "robots", content: data.robots || "index,follow,max-image-preview:large" });
    setMeta('meta[property="og:type"]', { property: "og:type", content: data.type || "website" });
    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    setMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_ZA" });
    setMeta('meta[property="og:title"]', { property: "og:title", content: data.title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: data.description });
    setMeta('meta[property="og:url"]', { property: "og:url", content: data.canonical });
    setMeta('meta[property="og:image"]', { property: "og:image", content: data.image || DEFAULT_IMAGE });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = data.canonical;

    let jsonLd = document.getElementById("seo-jsonld") as HTMLScriptElement | null;
    if (!jsonLd) {
      jsonLd = document.createElement("script");
      jsonLd.id = "seo-jsonld";
      jsonLd.type = "application/ld+json";
      document.head.appendChild(jsonLd);
    }
    jsonLd.text = JSON.stringify(data.schemas || []);
  }, [data]);

  return null;
}
